import React, { useState, useEffect } from "react";
import { fetchUsersByRole } from "../../../api";
import { USER_ROLES } from "../../constants";
import { FaUserFriends } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import "../PatientsList/PatientsList.css";

const RAList = ({ selectedRAId, onRAClick }) => {
  const [ras, setRAs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  const loadRAs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchUsersByRole(USER_ROLES.RA);
      // Handle different response structures
      const rasList = Array.isArray(response) 
        ? response 
        : (response?.users || response?.data || []);
      setRAs(rasList);
    } catch (error) {
      console.error("Error loading RAs:", error);
      setError("Failed to load RAs");
      setRAs([]); // Ensure ras is always an array
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRAs();
  }, []);

  const handleRAClick = (ra) => {
    if (onRAClick) {
      onRAClick(ra);
    }
  };

  const getAllRAs = () => {
    return ras || [];
  };

  const getFilteredRAs = () => {
    let filteredRAs = getAllRAs();

    // No active/inactive filtering since number_of_videos is not in API response
    filteredRAs = ras;

    if (searchInput) {
      filteredRAs = filteredRAs.filter(
        (ra) =>
          ra.full_name
            ?.toLowerCase()
            .includes(searchInput.toLowerCase()) ||
          ra.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
          ra.username?.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    return filteredRAs;
  };

  const getFilteredCounts = () => {
    const allRAs = ras || [];

    const filteredAll = searchInput
      ? allRAs.filter(
          (ra) =>
            ra.full_name
              ?.toLowerCase()
              .includes(searchInput.toLowerCase()) ||
            ra.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
            ra.username?.toLowerCase().includes(searchInput.toLowerCase())
        )
      : allRAs;

    return {
      total: filteredAll.length,
      active: 0,
      inactive: 0,
    };
  };



  if (loading) {
    return (
      <div className="patients-container">
        <div className="loading-message">Loading RAs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patients-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadRAs}>Retry</button>
        </div>
      </div>
    );
  }

  const filteredRAs = getFilteredRAs();
  const filteredCounts = getFilteredCounts();

  return (
    <div className="patients-container">
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon-wrapper first">
            <FaUserFriends className="stat-icon" />
          </div>
          <div className="stat-content">
            <span className="stat-label">
              Total RAs {searchInput && "(filtered)"}
            </span>
            <span className="stat-value">{filteredCounts.total}</span>
          </div>
        </div>
      </div>

      <div className="patients-table-container">
        <div className="table-header">
          <div className="table-controls">
            <div className="search-container">
              <IoSearchOutline className="search-icon" />
              <input
                placeholder="Search RAs..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        <div className="patients-table">
          <div className="table-header-row four-columns">
            <span className="header-cell">Name</span>
            <span className="header-cell">Email</span>
            <span className="header-cell">Assignee</span>
            <span className="header-cell">Actions</span>
          </div>

          {filteredRAs.map((ra, index) => (
            <div key={ra.username || ra.email || index} className="table-row four-columns">
              <span className="table-cell patient-name">
                {ra.full_name}
              </span>
              <span className="table-cell">{ra.email || 'N/A'}</span>
              <span className="table-cell">{ra.assigned_to || '—'}</span>
              <span className="table-cell">
                <button
                  className="action-button"
                  onClick={() => handleRAClick(ra)}
                >
                  View
                </button>
              </span>
            </div>
          ))}

          {filteredRAs.length === 0 && (
            <div className="no-data-row">
              <span className="no-data-message">
                {searchInput
                  ? "No RAs found matching your search."
                  : "No RAs available."}
              </span>
            </div>
          )}
        </div>

        <div className="table-footer">
          <span className="table-info">
            Showing {filteredRAs.length} of {filteredCounts.total} RAs
          </span>
        </div>
      </div>
    </div>
  );
};

export default RAList;
